export interface Spatial {
  x: number;
  y: number;
  r: number;
}

/**
 * Uniform grid spatial hash, rebuilt every tick. Cheap for a few thousand entities and
 * avoids O(n^2) collision scans.
 */
export class SpatialHash<T extends Spatial> {
  private cells = new Map<number, T[]>();
  constructor(private cellSize: number) {}

  clear(): void {
    this.cells.clear();
  }

  private key(cx: number, cy: number): number {
    return (cx + 32768) * 65536 + (cy + 32768);
  }

  insert(item: T): void {
    const cs = this.cellSize;
    const x0 = Math.floor((item.x - item.r) / cs);
    const x1 = Math.floor((item.x + item.r) / cs);
    const y0 = Math.floor((item.y - item.r) / cs);
    const y1 = Math.floor((item.y + item.r) / cs);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const k = this.key(cx, cy);
        let list = this.cells.get(k);
        if (!list) {
          list = [];
          this.cells.set(k, list);
        }
        list.push(item);
      }
    }
  }

  /** Collects items whose circle overlaps the query circle. Deduplicated. */
  queryCircle(x: number, y: number, r: number, out: T[] = []): T[] {
    const cs = this.cellSize;
    const x0 = Math.floor((x - r) / cs);
    const x1 = Math.floor((x + r) / cs);
    const y0 = Math.floor((y - r) / cs);
    const y1 = Math.floor((y + r) / cs);
    const seen = new Set<T>();
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const list = this.cells.get(this.key(cx, cy));
        if (!list) continue;
        for (const it of list) {
          if (seen.has(it)) continue;
          const dx = it.x - x;
          const dy = it.y - y;
          const rr = it.r + r;
          if (dx * dx + dy * dy <= rr * rr) {
            seen.add(it);
            out.push(it);
          }
        }
      }
    }
    return out;
  }

  /** Nearest item to (x, y) within maxDist that passes the filter. */
  nearest(x: number, y: number, maxDist: number, filter?: (it: T) => boolean): T | undefined {
    const cs = this.cellSize;
    const x0 = Math.floor((x - maxDist) / cs);
    const x1 = Math.floor((x + maxDist) / cs);
    const y0 = Math.floor((y - maxDist) / cs);
    const y1 = Math.floor((y + maxDist) / cs);
    let best: T | undefined;
    let bestD = maxDist * maxDist;
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const list = this.cells.get(this.key(cx, cy));
        if (!list) continue;
        for (const it of list) {
          if (filter && !filter(it)) continue;
          const dx = it.x - x;
          const dy = it.y - y;
          const d = dx * dx + dy * dy;
          if (d < bestD) {
            bestD = d;
            best = it;
          }
        }
      }
    }
    return best;
  }
}
